import { Link } from 'react-router-dom';

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-indigo-700 to-purple-800 text-white">
        <div className="max-w-6xl mx-auto px-4 py-24 md:py-32">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-6xl font-bold leading-tight">
              Email Marketing, <span className="text-yellow-300">Supercharged with AI</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-blue-100 max-w-2xl mx-auto">
              VSENDS combines powerful email marketing tools with intelligent AI automation. Create campaigns, build workflows, and grow your business — faster than ever.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/signup" className="px-8 py-3 bg-white text-blue-700 font-semibold rounded-xl hover:bg-blue-50 transition-colors text-lg">
                Get Started Free
              </Link>
              <Link to="/features" className="px-8 py-3 border-2 border-white text-white font-semibold rounded-xl hover:bg-white/10 transition-colors text-lg">
                See Features
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Trusted by businesses worldwide</p>
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '10M+', label: 'Emails Sent' },
              { value: '5,000+', label: 'Active Users' },
              { value: '99.9%', label: 'Uptime' },
              { value: '4.9/5', label: 'Customer Rating' },
            ].map((stat) => (
              <div key={stat.label}>
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-sm text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Everything You Need</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">From campaign creation to AI-powered automation, VSENDS has every tool your email marketing needs.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: '✉️', title: 'Campaign Builder', desc: 'Drag-and-drop email builder with templates, personalization, and A/B testing.' },
              { icon: '🤖', title: 'AI Assistant', desc: 'AI that writes emails, generates subject lines, and optimizes your campaigns.' },
              { icon: '⚡', title: 'Automations', desc: 'Build workflows for onboarding, abandoned carts, re-engagement, and more.' },
              { icon: '📊', title: 'Analytics', desc: 'Real-time dashboards with open rates, click rates, and engagement metrics.' },
              { icon: '👥', title: 'Contact Management', desc: 'Segments, tags, custom fields, and smart list management.' },
              { icon: '🔒', title: 'GDPR Compliant', desc: 'Built-in compliance tools, consent management, and data protection.' },
            ].map((feature) => (
              <div key={feature.title} className="p-6 bg-white border border-gray-200 rounded-xl hover:shadow-lg transition-shadow">
                <span className="text-3xl">{feature.icon}</span>
                <h3 className="text-lg font-semibold text-gray-900 mt-4">{feature.title}</h3>
                <p className="text-sm text-gray-600 mt-2">{feature.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/features" className="text-blue-600 font-medium hover:text-blue-700">
              View all features →
            </Link>
          </div>
        </div>
      </section>

      {/* AI Section */}
      <section className="py-20 bg-gradient-to-r from-indigo-50 to-purple-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-sm font-semibold text-indigo-600 uppercase tracking-wide">AI-Powered</span>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mt-4">Your AI Marketing Assistant</h2>
              <p className="mt-4 text-lg text-gray-600">
                VSENDS AI understands your business and helps you create better campaigns, faster. Just tell it what you need.
              </p>
              <ul className="mt-6 space-y-3">
                {[
                  'Write complete emails in seconds',
                  'Generate high-converting subject lines',
                  'Build automation workflows from scratch',
                  'Analyze campaign performance',
                  'Suggest optimal segments and send times',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/signup" className="inline-block mt-8 px-6 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition-colors">
                Try AI Features
              </Link>
            </div>
            <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm">👤</div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-sm">
                    <p className="text-sm text-gray-900">Create a welcome email sequence for new subscribers</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 justify-end">
                  <div className="bg-blue-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-sm">
                    <p className="text-sm text-white">I'll create a 4-email welcome sequence: immediate welcome, getting started guide, pro tips, and a feedback request. Each email is personalized and optimized for engagement.</p>
                  </div>
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-sm">🤖</div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-sm">👤</div>
                  <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 max-w-sm">
                    <p className="text-sm text-gray-900">Looks great! Set it up.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 justify-end">
                  <div className="bg-blue-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-sm">
                    <p className="text-sm text-white">✅ Welcome sequence created and activated! All 4 emails are scheduled with optimal timing.</p>
                  </div>
                  <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center text-sm">🤖</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900">Simple, Transparent Pricing</h2>
            <p className="mt-4 text-lg text-gray-600">Start free. Scale as you grow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {[
              { name: 'Free', price: '$0', period: '/month', features: ['1,000 contacts', '5,000 emails/month', 'Basic templates', 'Email support'], cta: 'Get Started', highlighted: false },
              { name: 'Starter', price: '$29', period: '/month', features: ['10,000 contacts', '50,000 emails/month', 'AI features', 'Advanced analytics', 'Priority support'], cta: 'Start Free Trial', highlighted: true },
              { name: 'Pro', price: '$79', period: '/month', features: ['50,000 contacts', 'Unlimited emails', 'Full AI suite', 'Custom domains', 'Dedicated support'], cta: 'Start Free Trial', highlighted: false },
            ].map((plan) => (
              <div key={plan.name} className={`p-8 rounded-2xl border-2 ${plan.highlighted ? 'border-blue-600 bg-blue-50 shadow-lg' : 'border-gray-200 bg-white'}`}>
                <h3 className="text-lg font-semibold text-gray-900">{plan.name}</h3>
                <p className="mt-2">
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500">{plan.period}</span>
                </p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/signup"
                  className={`mt-8 block text-center px-6 py-3 rounded-xl font-medium transition-colors ${
                    plan.highlighted
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/pricing" className="text-blue-600 font-medium hover:text-blue-700">
              View full pricing →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold">Ready to Transform Your Email Marketing?</h2>
          <p className="mt-4 text-lg text-gray-300 max-w-2xl mx-auto">
            Join thousands of businesses using VSENDS to create better emails, automate workflows, and grow their audience with AI.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/signup" className="px-8 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors text-lg">
              Start Free Today
            </Link>
            <Link to="/contact" className="px-8 py-3 border-2 border-gray-600 text-white font-semibold rounded-xl hover:bg-gray-800 transition-colors text-lg">
              Contact Sales
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
